import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, LogIn, LogOut, CheckCircle2, FileText, Download, Briefcase, Megaphone } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { Reveal, GoldLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#F5F2EB] placeholder-[#A5A5A5]/50 px-5 py-4 text-sm font-light focus:outline-none focus:border-[#E5C776] focus:ring-1 focus:ring-[#E5C776] transition-colors duration-300";

const statoLabel = (s) =>
  ({ nuova: "Nuova", contattata: "Contattata", "in-trattativa": "In trattativa", chiusa: "Chiusa" }[s] || s);

const PartnerArea = ({ user, partnership, onLogout }) => {
  const [documenti, setDocumenti] = useState([]);
  const [opportunita, setOpportunita] = useState([]);
  const [comunicazioni, setComunicazioni] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    axios.get(`${API}/documenti`, { withCredentials: true }).then(({ data }) => setDocumenti(data)).catch(() => {});
    axios.get(`${API}/opportunita`, { withCredentials: true }).then(({ data }) => setOpportunita(data)).catch(() => {});
    axios.get(`${API}/comunicazioni`, { withCredentials: true }).then(({ data }) => setComunicazioni(data)).catch(() => {});
  }, []);

  const scarica = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await axios.get(`${API}/documenti/${doc.id}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.titolo;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    } finally {
      setDownloading(null);
    }
  };

  return (
  <div className="w-full max-w-3xl py-32" data-testid="partner-area">
    <Reveal>
      <img src={LOGO_URL} alt="Billions Group" className="h-16 w-auto mx-auto mb-8" />
    </Reveal>
    <Reveal delay={0.1}>
      <p className="eyebrow text-center mb-4">Area Riservata</p>
      <h1 className="font-display text-4xl sm:text-5xl font-light text-[#F5F2EB] text-center tracking-tight">
        Benvenuto, <span className="gold-gradient-text italic">{user.name}.</span>
      </h1>
    </Reveal>
    <GoldLine className="mt-8 w-24 mx-auto" />
    <Reveal delay={0.2}>
      <div className="mt-10 bg-[#141414] border border-[#C6A45D]/25 p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle2 size={18} className="text-[#C6A45D]" />
          <p className="text-sm text-[#F5F2EB]">Accesso partner attivo</p>
        </div>
        {partnership && (
          <dl className="space-y-4 text-sm font-light">
            {partnership.azienda && (
              <div className="flex justify-between gap-4">
                <dt className="text-[#A5A5A5]">Azienda</dt>
                <dd className="text-[#F5F2EB] text-right">{partnership.azienda}</dd>
              </div>
            )}
            {partnership.divisione && (
              <div className="flex justify-between gap-4">
                <dt className="text-[#A5A5A5]">Business Unit di interesse</dt>
                <dd className="text-[#F5F2EB] text-right">Billions {partnership.divisione}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-[#A5A5A5]">Stato candidatura</dt>
              <dd className="text-[#E5C776] text-right">{statoLabel(partnership.stato)}</dd>
            </div>
          </dl>
        )}
        <div className="gold-line my-8 opacity-60" />
        <p className="text-sm text-[#A5A5A5] font-light leading-relaxed">
          Di seguito trovate i documenti riservati e le opportunità che il
          Gruppo condivide con i partner approvati.
        </p>
      </div>
    </Reveal>

    <Reveal delay={0.25}>
      <h2 className="font-display text-2xl font-light text-[#F5F2EB] flex items-center gap-3 mb-5">
        <Megaphone size={19} className="text-[#C6A45D]" />
        Comunicazioni dal Gruppo
      </h2>
    </Reveal>
    {comunicazioni.length === 0 ? (
      <p className="text-sm text-[#A5A5A5]/70 font-light">Nessuna comunicazione al momento.</p>
    ) : (
      <div className="space-y-3 mb-2" data-testid="partner-comunicazioni-list">
        {comunicazioni.map((c) => (
          <div
            key={c.id}
            className="bg-[#141414] border-l-2 border-l-[#C6A45D] border border-[#C6A45D]/20 p-5 sm:p-6"
          >
            <p className="font-display text-lg text-[#F5F2EB]">{c.titolo}</p>
            <p className="text-xs text-[#A5A5A5]/70 mt-1">
              {new Date(c.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
            </p>
            <p className="mt-3 text-sm text-[#A5A5A5] font-light leading-relaxed">{c.testo}</p>
          </div>
        ))}
      </div>
    )}

    <Reveal delay={0.28}>
      <h2 className="font-display text-2xl font-light text-[#F5F2EB] flex items-center gap-3 mt-12 mb-5">
        <FileText size={19} className="text-[#C6A45D]" />
        Documenti riservati
      </h2>
    </Reveal>
    {documenti.length === 0 ? (
      <p className="text-sm text-[#A5A5A5]/70 font-light">Nessun documento disponibile al momento.</p>
    ) : (
      <div className="space-y-3" data-testid="partner-documenti-list">
        {documenti.map((d) => (
          <div
            key={d.id}
            className="bg-[#141414] border border-[#C6A45D]/20 hover:border-[#C6A45D]/60 transition-colors duration-300 p-5 flex items-center gap-4"
          >
            <FileText size={18} className="text-[#C6A45D] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#F5F2EB] truncate">{d.titolo}</p>
              <p className="text-xs text-[#A5A5A5]/70 mt-0.5">{(d.size / 1024).toFixed(0)} KB</p>
            </div>
            <button
              onClick={() => scarica(d)}
              disabled={downloading === d.id}
              data-testid={`partner-doc-download-${d.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#C6A45D]/60 text-[#C6A45D] text-[10px] tracking-[0.25em] uppercase hover:bg-[#C6A45D] hover:text-[#080808] disabled:opacity-50 transition-colors duration-300"
            >
              <Download size={13} /> {downloading === d.id ? "..." : "Scarica"}
            </button>
          </div>
        ))}
      </div>
    )}

    <Reveal delay={0.3}>
      <h2 className="font-display text-2xl font-light text-[#F5F2EB] flex items-center gap-3 mt-12 mb-5">
        <Briefcase size={19} className="text-[#C6A45D]" />
        Opportunità del Gruppo
      </h2>
    </Reveal>
    {opportunita.length === 0 ? (
      <p className="text-sm text-[#A5A5A5]/70 font-light">Nessuna opportunità pubblicata al momento.</p>
    ) : (
      <div className="space-y-3" data-testid="partner-opportunita-list">
        {opportunita.map((o) => (
          <div
            key={o.id}
            className="bg-[#141414] border border-[#C6A45D]/20 hover:border-[#C6A45D]/60 transition-colors duration-300 p-5 sm:p-6"
          >
            <p className="font-display text-lg text-[#F5F2EB]">{o.titolo}</p>
            <p className="text-xs text-[#C6A45D] tracking-[0.15em] uppercase mt-1">
              {o.divisione ? `Billions ${o.divisione}` : "Trasversale"}
              {o.scadenza ? ` · Scadenza ${o.scadenza}` : ""}
            </p>
            <p className="mt-3 text-sm text-[#A5A5A5] font-light leading-relaxed">{o.descrizione}</p>
          </div>
        ))}
      </div>
    )}

    <Reveal delay={0.35}>
      <button
        onClick={onLogout}
        data-testid="partner-logout-button"
        className="mt-8 mx-auto flex items-center gap-2 px-8 py-3 border border-[#A5A5A5]/30 text-[#A5A5A5] text-xs tracking-[0.25em] uppercase hover:border-[#C6A45D] hover:text-[#C6A45D] transition-colors duration-300"
      >
        <LogOut size={14} />
        Esci
      </button>
    </Reveal>
  </div>
  );
};

export default function AreaRiservata() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [partnership, setPartnership] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/partner/area`, { withCredentials: true })
      .then(({ data }) => {
        if (data.user.role === "admin") {
          navigate("/admin");
          return;
        }
        setUser(data.user);
        setPartnership(data.partnership);
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.role === "admin") {
        navigate("/admin");
        return;
      }
      const area = await axios.get(`${API}/partner/area`, { withCredentials: true });
      setUser(area.data.user);
      setPartnership(area.data.partnership);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setPartnership(null);
      setEmail("");
      setPassword("");
    }
  };

  return (
    <main data-testid="area-riservata-page" className="bg-[#080808] min-h-screen flex items-center justify-center px-6">
      {checking ? (
        <Loader2 size={22} className="text-[#C6A45D] animate-spin" />
      ) : user ? (
        <PartnerArea user={user} partnership={partnership} onLogout={logout} />
      ) : (
        <div className="w-full max-w-md py-32">
          <Reveal>
            <img src={LOGO_URL} alt="Billions Group" className="h-16 w-auto mx-auto mb-8" />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="w-14 h-14 mx-auto border border-[#C6A45D]/40 flex items-center justify-center mb-6">
              <Lock size={20} className="text-[#C6A45D]" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="font-display text-3xl sm:text-4xl font-light text-[#F5F2EB] text-center tracking-tight">
              Area <span className="gold-gradient-text italic">Riservata</span>
            </h1>
          </Reveal>
          <GoldLine className="mt-6 w-20 mx-auto" />
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm text-[#A5A5A5] font-light text-center leading-relaxed">
              Accesso riservato ai partner approvati dal Gruppo. Le credenziali
              vengono attivate dal nostro team dopo la valutazione della
              candidatura.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <form
              onSubmit={submit}
              data-testid="partner-login-form"
              className="mt-8 bg-[#141414] border border-[#C6A45D]/20 p-8 space-y-6"
            >
              {error && (
                <div data-testid="partner-login-error" className="border border-red-900/50 bg-[#1D1D1D] px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="partner-login-email"
                className={inputCls}
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="partner-login-password"
                className={inputCls}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="partner-login-submit"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] disabled:opacity-60 transition-colors duration-300"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <LogIn size={15} />}
                Accedi
              </button>
            </form>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 text-center text-xs text-[#A5A5A5]/80 font-light">
              Non siete ancora partner?{" "}
              <Link to="/diventa-partner" data-testid="area-riservata-link-partner" className="text-[#C6A45D] hover:text-[#E5C776] underline underline-offset-4 transition-colors duration-300">
                Candidatevi qui
              </Link>
            </p>
          </Reveal>
        </div>
      )}
    </main>
  );
}
